import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

export type StatTileAccent = 'metal' | 'mineral' | 'gas' | 'energy' | 'population' | 'resource' | 'industry' | 'military';
export type StatTileStatus = 'normal' | 'warning' | 'danger';

export interface StatTileProps extends HTMLAttributes<HTMLDivElement> {
    icon: ReactNode;
    value: ReactNode;
    sublabel: ReactNode;
    accent: StatTileAccent;
    status?: StatTileStatus;
}

const ACCENT_CLASSES: Record<StatTileAccent, string> = {
    metal: 'text-metal',
    mineral: 'text-mineral',
    gas: 'text-gas',
    energy: 'text-energy',
    // The mockup strokes the population glyph #e6e8f5, the same weight as
    // the four resource glyphs carry in their own hues. content-secondary
    // made it recessive against them.
    population: 'text-content-primary',
    resource: 'text-zone-resource',
    industry: 'text-zone-industry',
    military: 'text-zone-military',
};

const STATUS_VALUE_CLASSES: Record<StatTileStatus, string> = {
    normal: 'text-content-primary',
    warning: 'text-status-warning',
    danger: 'text-status-danger',
};

const BASE_CLASSES = 'inline-flex items-center gap-2 bg-surface-chip border border-white/[0.08] rounded-chip px-2.5 py-1.5';

export const StatTile = forwardRef<HTMLDivElement, StatTileProps>(function StatTile(
    { icon, value, sublabel, accent, status = 'normal', className, ...rest },
    ref,
) {
    return (
        <div ref={ref} className={cn(BASE_CLASSES, className)} {...rest}>
            <span className={cn('h-[17px] w-[17px] shrink-0', ACCENT_CLASSES[accent])}>{icon}</span>
            <span className="flex flex-col leading-tight">
                <span className={cn('text-value', STATUS_VALUE_CLASSES[status])}>{value}</span>
                <span className="text-micro text-content-muted">{sublabel}</span>
            </span>
        </div>
    );
});

export default StatTile;
